import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';

/**
 * Options for sending an email
 */
export interface SendMailOptions {
  /**
   * Recipient email address
   */
  to: string;

  /**
   * Email subject line
   */
  subject: string;

  /**
   * Plain text version of the email body
   */
  text: string;

  /**
   * HTML version of the email body (optional)
   * If not provided, text will be converted to basic HTML
   */
  html?: string;
}

/**
 * Result of a sendMail operation
 */
export interface SendMailResult {
  /**
   * Unique identifier for tracking the email
   */
  mailId: string;

  /**
   * Current status of the email
   */
  status: 'pending' | 'sent' | 'error';

  /**
   * Timestamp when the email was sent (if successful)
   */
  sentAt?: string;

  /**
   * Error message if the email failed to send
   */
  error?: string;
}

/**
 * Validates an email address format
 * @param email The email address to validate
 * @returns boolean indicating if the email is valid
 */
export function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Sends an email using Firebase's Trigger Email extension
 *
 * @param options Options for sending the email
 * @returns Promise resolving to the send result with tracking ID
 * @throws Error if the email is invalid or sending fails
 */
export async function sendMail(options: SendMailOptions): Promise<SendMailResult> {
  const { to, subject, text, html } = options;

  // Validate email address
  if (!to || !isValidEmail(to)) {
    throw new Error(`Invalid email address: ${to}`);
  }

  // Generate unique ID for tracking
  const mailId = crypto.randomUUID();

  // Create email document using the structure required by Firebase Extension
  const email = {
    to,
    message: {
      subject,
      text,
      html: html || text.replace(/\n/g, '<br>'),
    },
    status: 'pending',
  };

  try {
    // Add email to the collection monitored by Firebase Extension
    const emailDocRef = doc(db, 'mail', mailId);
    await setDoc(emailDocRef, email);

    // Poll for completion with timeout
    const maxAttempts = 10; // Try up to 10 times
    const pollInterval = 1000; // Check every second
    let attempts = 0;

    while (attempts < maxAttempts) {
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
      attempts++;

      const emailDoc = await getDoc(emailDocRef);
      const sentEmail = emailDoc.data();

      if (!sentEmail) {
        if (attempts === maxAttempts) {
          throw new Error('Email document not found after maximum attempts');
        }
        continue;
      }

      // Check delivery status from Firebase extension
      const deliveryState = sentEmail.delivery?.state;

      if (deliveryState === 'SUCCESS') {
        return {
          mailId,
          status: 'sent',
          sentAt: sentEmail.delivery?.completedAt || new Date().toISOString(),
        };
      } else if (deliveryState === 'ERROR' || deliveryState === 'FAILED') {
        const errorMessage = sentEmail.delivery?.error
          ? typeof sentEmail.delivery.error === 'string'
            ? sentEmail.delivery.error
            : sentEmail.delivery.error.message || 'Unknown error'
          : 'Email sending failed';

        console.error('Email sending failed:', {
          mailId,
          deliveryState,
          errorMessage,
        });

        return {
          mailId,
          status: 'error',
          error: errorMessage,
        };
      }

      // If we get here, the email is still processing
      if (attempts === maxAttempts) {
        return {
          mailId,
          status: 'error',
          error: 'Email sending timed out after ' + maxAttempts + ' seconds',
        };
      }
    }

    return {
      mailId,
      status: 'error',
      error: 'Email sending timed out',
    };
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error sending email';
    console.error('Error in sendMail:', errorMessage);

    return {
      mailId,
      status: 'error',
      error: errorMessage,
    };
  }
}
